

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Arrange Free React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Arrange Free React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
// import SidenavCard from "examples/Sidenav/SidenavCard";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Arrange Free React context
import { useSoftUIController, setMiniSidenav } from "context";
import { Collapse } from "antd";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const [expanded, setexpanded] = useState(false)
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderTitleHeader = (label, iconElement) => (
    <SoftTypography
      style={{ margin: 0, marginTop: 5 }}
      display="flex"
      gap={2}
      variant="caption"
      fontWeight="bold"
      textTransform="uppercase"
      opacity={0.6}
      pl={3}
      mt={2}
      mb={1}
      ml={1}
    >
      {iconElement}
      {label}
    </SoftTypography>
  );

  const renderSideItem = (item) => {
    if (!item) {
      return null;
    }

    if (item.type === "collapse") {
      if (item.href) {
        return (
          <Link
            href={item.href}
            key={item.key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={item.name}
              icon={item.icon}
              active={item.key === collapseName}
              noCollapse={item.noCollapse}
            />
          </Link>
        );
      }

      if (item.route) {
        return (
          <NavLink to={item.route} key={item.key} state={item.state}>
            <SidenavCollapse
              color={color}
              key={item.key}
              name={item.name}
              icon={item.icon}
              active={item.key === collapseName}
              noCollapse={item.noCollapse}
            />
          </NavLink>
        );
      }

      return null;
    }

    if (item.type === "title") {
      return (
        <Collapse ghost expandIconPosition="end" key={item.key}>
          <Collapse.Panel header={renderTitleHeader(item.title, item.icon)}>
            {item.children?.map((nestedItem) => renderSideItem(nestedItem))}
          </Collapse.Panel>
        </Collapse>
      );
    }

    if (item.type === "divider") {
      return <Divider key={item.key} />;
    }

    return null;
  };

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map((route) => renderSideItem(route));

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt="Soft UI Logo" width="2rem" />}
          <SoftBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <SoftTypography component="h6" variant="button" fontWeight="medium">
              {brandName}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Divider />
      <List>{renderRoutes}</List>
      <SoftBox pt={2} my={2} mx={2} mt="auto">
      </SoftBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
